function isNonEmptyArray(value) {
  return Array.isArray(value) && value.length > 0;
}

const ORIGINAL_SOLD_STATUSES = new Set([
  'sold',
  'original_sold',
  'sold_admin_only',
  'sold_out',
  'auction-sold',
  'auction_original_sold',
  'auction_sold_out',
]);

export function getArtworkViewerContext(artworkData) {
  return artworkData?.viewer_context ?? null;
}

export function getArtworkPurchaseContext(artworkData) {
  return artworkData?.purchase_context ?? null;
}

export function getArtworkSaleSummary(artworkData) {
  const sale = getArtworkPurchaseContext(artworkData)?.artwork_sale
    ?? artworkData?.artwork_sale
    ?? null;

  if (!sale || typeof sale !== 'object') {
    return null;
  }

  const price = Number(sale.price);
  return Number.isFinite(price) && price >= 0
    ? { price, currency: sale.currency ?? null }
    : null;
}

export function getArtworkOwnershipContext(artworkData) {
  return artworkData?.ownership_context ?? null;
}

function normalizeEntityId(candidate) {
  if (candidate === null || candidate === undefined) {
    return null;
  }
  if (typeof candidate === 'string' || typeof candidate === 'number') {
    const normalized = String(candidate).trim();
    return normalized || null;
  }
  if (typeof candidate !== 'object') {
    return null;
  }
  if (candidate._id !== undefined && candidate._id !== null) {
    return normalizeEntityId(candidate._id);
  }
  if (candidate.id !== undefined && candidate.id !== null) {
    return normalizeEntityId(candidate.id);
  }
  if (typeof candidate.toString === 'function') {
    const normalized = String(candidate.toString()).trim();
    if (normalized && normalized !== '[object Object]') {
      return normalized;
    }
  }
  return null;
}

function getViewerEntityId(userSession) {
  return normalizeEntityId(userSession?._id ?? userSession?.id ?? null);
}

export function getArtworkStatus(artworkData) {
  const purchaseContextStatus = getArtworkPurchaseContext(artworkData)?.artwork_status;
  if (typeof purchaseContextStatus === 'string') {
    return purchaseContextStatus;
  }
  return typeof artworkData?.artwork_status === 'string'
    ? artworkData.artwork_status
    : null;
}

export function getNormalizedArtworkStatus(artworkData) {
  const status = getArtworkStatus(artworkData);
  return typeof status === 'string' ? status.toLowerCase() : '';
}

export function getArtworkFlags(artworkData) {
  return artworkData?.artwork_flags ?? {};
}

export function hasArtworkSoldMetadata(artworkData) {
  const flags = getArtworkFlags(artworkData);
  const soldAt = flags.sold_at ?? flags.soldAt ?? null;
  const soldVia = flags.sold_via ?? flags.soldVia ?? null;
  const soldPrice = flags.sold_price ?? flags.soldPrice ?? null;
  const soldCurrency = flags.sold_currency ?? flags.soldCurrency ?? null;

  return Boolean(soldAt || soldVia || soldPrice || soldCurrency);
}

export function isArtworkOriginalSold(artworkData) {
  const status = getNormalizedArtworkStatus(artworkData);

  return status
    ? ORIGINAL_SOLD_STATUSES.has(status)
    : hasArtworkSoldMetadata(artworkData);
}

export function getArtworkHoldingContext(artworkData) {
  if (!artworkData) {
    return { holdingId: null, ownerId: null, holdingType: null };
  }

  const ownershipContext = getArtworkOwnershipContext(artworkData);
  const ownershipHoldingContext = ownershipContext?.holding_context ?? {};
  const ownershipMetadata = ownershipContext?.product_metadata ?? {};

  return {
    holdingId:
      ownershipHoldingContext?.holding_id
      ?? ownershipMetadata?.holding_id
      ?? artworkData?.metadata?.holding_id
      ?? artworkData?.product_metadata?.holding_id
      ?? artworkData?.holding_id
      ?? null,
    ownerId:
      ownershipHoldingContext?.owner_id
      ?? ownershipMetadata?.owner_id
      ?? artworkData?.metadata?.owner_id
      ?? artworkData?.product_metadata?.owner_id
      ?? artworkData?.owner_id
      ?? null,
    holdingType:
      ownershipHoldingContext?.holding_type
      ?? ownershipMetadata?.holding_type
      ?? artworkData?.metadata?.holding_type
      ?? artworkData?.product_metadata?.holding_type
      ?? artworkData?.holding_type
      ?? null,
  };
}

export function getArtworkHoldingType(artworkData) {
  return getArtworkHoldingContext(artworkData).holdingType;
}

export function isLimitedEditionHoldingArtwork(artworkData) {
  return getArtworkHoldingType(artworkData) === 'limited_edition';
}

export function getArtworkGalleryId(artworkData) {
  const viewerContextGalleryId = normalizeEntityId(getArtworkViewerContext(artworkData)?.gallery?._id);
  if (viewerContextGalleryId) {
    return viewerContextGalleryId;
  }
  const purchaseContextGalleryId = normalizeEntityId(getArtworkPurchaseContext(artworkData)?.artwork_gallery?._id);
  if (purchaseContextGalleryId) {
    return purchaseContextGalleryId;
  }
  return normalizeEntityId(
    artworkData?.artwork_about?.artwork_gallery?._id
    ?? artworkData?.artwork_about?.artwork_gallery
    ?? null,
  );
}

export function getArtworkDisplayedArtistId(artworkData) {
  const viewerContext = getArtworkViewerContext(artworkData);
  const viewerArtistId = normalizeEntityId(
    viewerContext?.artist?._id
    ?? viewerContext?.gallery_artist?._id
    ?? null,
  );
  if (viewerArtistId) {
    return viewerArtistId;
  }
  const artworkAbout = artworkData?.artwork_about ?? {};
  return normalizeEntityId(
    artworkAbout?.artwork_artist
    ?? artworkAbout?.artwork_gallery_artist?._id
    ?? artworkAbout?.artwork_gallery_artist?.id
    ?? null,
  );
}

export function getArtworkArtistModalData(artworkData) {
  const artworkAbout = artworkData?.artwork_about ?? {};
  if (artworkAbout?.artwork_artist) {
    return {
      ...artworkAbout.artwork_artist,
      ...artworkAbout.artwork_gallery_artist,
      user_type: 'artist',
    };
  }
  if (artworkAbout?.artwork_gallery_artist) {
    return {
      ...artworkAbout.artwork_gallery_artist,
      user_type: 'gallery_artist',
    };
  }
  return null;
}

export function isViewerArtworkGalleryOwner(artworkData, userSession = {}) {
  const contextualValue = getArtworkViewerContext(artworkData)?.is_gallery_owner_view;
  if (typeof contextualValue === 'boolean') {
    return contextualValue;
  }
  const viewerId = getViewerEntityId(userSession);
  const galleryId = getArtworkGalleryId(artworkData);
  return Boolean(viewerId && galleryId && viewerId === galleryId);
}

export function isViewerDisplayedArtistOwner(artworkData, userSession = {}) {
  const contextualValue = getArtworkViewerContext(artworkData)?.is_displayed_artist_owner_view;
  if (typeof contextualValue === 'boolean') {
    return contextualValue;
  }
  const viewerId = getViewerEntityId(userSession);
  const artistId = getArtworkDisplayedArtistId(artworkData);
  return Boolean(viewerId && artistId && viewerId === artistId);
}

export function getArtworkTransactionSource(artworkData = {}) {
  const candidate = artworkData || {};
  const ownershipContext = getArtworkOwnershipContext(candidate);
  const publicSaleHistory = ownershipContext?.public_sale_history;

  if (Array.isArray(publicSaleHistory)) {
    return publicSaleHistory;
  }

  return (
    ownershipContext?.transaction_history
    ?? ownershipContext?.product_metadata?.transaction_history
    ?? candidate.transaction_history
    ?? candidate.data?.transaction_history
    ?? candidate.transactionHistory
    ?? candidate.transactions
    ?? candidate.data?.transactionHistory
    ?? candidate.data?.transactions
    ?? candidate.product_metadata?.transaction_history
    ?? candidate.data?.product_metadata?.transaction_history
    ?? candidate.product_metadata
    ?? candidate.data?.product_metadata
    ?? null
  );
}

export function getArtworkShippingConfig(artworkData) {
  const shipping = getArtworkPurchaseContext(artworkData)?.artwork_shipping
    ?? artworkData?.artwork_shipping
    ?? {};
  return {
    artwork_shipping_own: shipping.artwork_shipping_own ?? {},
    artwork_shipping_rest: shipping.artwork_shipping_rest ?? {},
    artwork_shipping_exceptions: Array.isArray(shipping.artwork_shipping_exceptions)
      ? shipping.artwork_shipping_exceptions
      : [],
    artwork_shipping_collectors_info: shipping.artwork_shipping_collectors_info ?? null,
  };
}

export function getArtworkPurchaseDetails(artworkData) {
  const purchaseContext = getArtworkPurchaseContext(artworkData);
  const artworkAbout = artworkData?.artwork_about ?? {};
  const gallery = purchaseContext?.artwork_gallery
    ?? artworkAbout?.artwork_gallery
    ?? null;

  return {
    status: getArtworkStatus(artworkData),
    type: purchaseContext?.artwork_type ?? artworkAbout?.artwork_type ?? null,
    currency: purchaseContext?.artwork_currency ?? artworkAbout?.artwork_currency ?? 'EUR',
    price: purchaseContext?.artwork_price ?? artworkAbout?.artwork_price ?? null,
    originalPrice: purchaseContext?.artwork_original_price ?? artworkAbout?.artwork_original_price ?? null,
    isLimitedEdition: purchaseContext?.artwork_limited_edition === true
      || artworkAbout?.artwork_limited_edition === true,
    isAuction: purchaseContext?.artwork_is_auction === true
      || artworkAbout?.artwork_is_auction === true,
    gallery,
    galleryName: gallery?.user_name ?? null,
    galleryCountry: gallery?.gallery_address?.country ?? null,
    galleryTaxEnabled: gallery?.gallery_tax_enabled === true,
    galleryClosed: gallery?.gallery_closed ?? null,
  };
}

export function getArtworkPurchaseGalleryProfile(artworkData) {
  const { gallery, galleryName, galleryCountry, galleryClosed } = getArtworkPurchaseDetails(artworkData);

  return {
    id: gallery?._id ?? null,
    username: gallery?.user_username ?? null,
    name: galleryName,
    country: galleryCountry,
    city: gallery?.gallery_address?.city ?? null,
    region: gallery?.gallery_address?.region ?? null,
    imageId: gallery?.user_image?.image_id ?? null,
    galleryClosed,
  };
}

export function getArtworkLimitedEditionOptions(artworkData, options = {}) {
  const candidates = [
    artworkData?.artwork_limited_editions,
    artworkData?.artwork_about?.artwork_limited_editions,
    getArtworkPurchaseContext(artworkData)?.artwork_limited_editions,
    artworkData?.product_metadata?.limitedEditionOptions,
    artworkData?.product_metadata?.limitedEdition?.editions,
    artworkData?.product_metadata?.limitedEdition?.options,
    artworkData?.product_metadata?.limited_edition?.editions,
    artworkData?.product_metadata?.limited_edition?.options,
    artworkData?.metadata?.limitedEdition?.editions,
    artworkData?.metadata?.limited_edition?.editions,
  ];

  for (let index = 0; index < candidates.length; index += 1) {
    const candidate = candidates[index];
    if (candidate && typeof candidate === 'object' && Object.keys(candidate).length > 0) {
      return candidate;
    }
  }

  if (options?.fallbackHoldingMetadata === true) {
    const limitedEditionMetadata =
      artworkData?.metadata?.limited_edition
      ?? artworkData?.metadata?.limitedEdition
      ?? artworkData?.product_metadata?.limited_edition
      ?? artworkData?.product_metadata?.limitedEdition
      ?? null;

    if (limitedEditionMetadata && typeof limitedEditionMetadata === 'object') {
      const sizeKey = (limitedEditionMetadata.size_key ?? limitedEditionMetadata.size ?? 'edition').toString();
      return {
        [sizeKey]: {
          size: limitedEditionMetadata.size ?? limitedEditionMetadata.size_key ?? 'Edition',
          quantity:
            limitedEditionMetadata.edition_size
            ?? limitedEditionMetadata.quantity
            ?? limitedEditionMetadata.total
            ?? null,
          sold: limitedEditionMetadata.sold ?? 0,
          price:
            artworkData?.metadata?.last_purchase?.price
            ?? artworkData?.product_metadata?.last_purchase?.price
            ?? null,
        },
      };
    }
  }

  return {};
}

export function getLimitedEditionSizeMap(options) {
  if (!options || typeof options !== 'object') {
    return {};
  }

  const normalizeSizeKey = (value) => (
    typeof value === 'string' ? value.trim().toLowerCase().replace(/\s+/g, '_') : null
  );
  const addSize = (sizeMap, key, quantity) => {
    const normalizedKey = normalizeSizeKey(key) ?? key;
    if (normalizedKey && Number.isFinite(quantity) && sizeMap[normalizedKey] === undefined) {
      sizeMap[normalizedKey] = quantity;
    }
  };

  return Object.entries(options).reduce((sizeMap, [key, option]) => {
    if (!option) {
      return sizeMap;
    }

    const quantity = Number(option.quantity ?? option.edition_size ?? option.editionSize);
    if (Number.isFinite(quantity) && quantity > 0) {
      addSize(sizeMap, key, quantity);
      addSize(sizeMap, option.size, quantity);
      addSize(sizeMap, option.size_label, quantity);
      addSize(sizeMap, option.sizeLabel, quantity);
      addSize(sizeMap, option.size_key, quantity);
      addSize(sizeMap, option.sizeKey, quantity);
      addSize(sizeMap, option.name, quantity);
    }

    return sizeMap;
  }, {});
}

export function isLimitedEditionAvailable(option) {
  if (!option || typeof option !== 'object') {
    return false;
  }

  if (typeof option.available === 'boolean') {
    return option.available;
  }

  const quantity = Number(option.quantity);
  const sold = Number(option.sold);
  return !Number.isFinite(quantity) || !Number.isFinite(sold) || sold < quantity;
}

/**
 * Returns public sold-out edition records only after the entire edition set has
 * completed. This keeps live sales progress out of presentation code.
 */
export function getFullySoldOutLimitedEditionEntries(options) {
  if (!options || typeof options !== 'object') {
    return [];
  }

  const entries = Object.entries(options)
    .map(([key, option]) => ({ key, option }))
    .filter(({ option }) => option && typeof option === 'object');

  if (!entries.length) {
    return [];
  }

  const soldOutEntries = entries.map(({ key, option }) => {
    const quantity = Number(option.quantity);
    const sold = Number(option.sold);
    const isConfirmedSoldOut = Number.isFinite(quantity)
      && quantity > 0
      && Number.isFinite(sold)
      && sold >= quantity
      && !isLimitedEditionAvailable(option);

    return isConfirmedSoldOut
      ? { key, option, soldCount: quantity }
      : null;
  });

  return soldOutEntries.every(Boolean) ? soldOutEntries : [];
}

export function getArtworkAiView(artworkData) {
  const artworkAi = artworkData?.artwork_ai ?? null;
  const visualDescription = artworkAi?.visual_description ?? null;
  const fallbackCanOpenAskAi =
    typeof visualDescription === 'string' && visualDescription.trim().length > 0;

  return {
    visualDescription,
    canOpenAskAi: typeof artworkAi?.can_open_ask_ai === 'boolean'
      ? artworkAi.can_open_ask_ai
      : fallbackCanOpenAskAi,
  };
}

export function getArtworkLimitedEditionHoldings(artworkData) {
  const candidates = [
    getArtworkOwnershipContext(artworkData)?.limited_edition_holdings,
    artworkData?.LEs_holdings,
    artworkData?.les_holdings,
    artworkData?.limitedEditionHoldings,
    artworkData?.limited_edition_holdings,
    artworkData?.artwork_LEs_holdings,
    artworkData?.artwork_limited_edition_holdings,
  ];

  for (let index = 0; index < candidates.length; index += 1) {
    const candidate = candidates[index];
    if (isNonEmptyArray(candidate)) {
      return candidate;
    }
  }

  return [];
}
